import React from "react";

const displayValue = (v) => (v != null && v !== "" ? String(v) : "—");

// Map API status/reason values to user-friendly labels (Title Case, readable)
const STATUS_LABELS = {
  ENABLED: "Enabled",
  ACTIVE: "Enabled", // legacy alias
  PAUSED: "Paused",
  REMOVED: "Removed",
  DRAFT: "Draft",
  ELIGIBLE: "Eligible",
  PENDING: "Pending",
  NOT_ELIGIBLE: "Not eligible",
  SERVING: "Serving",
  NONE: "None",
  ENDED: "Ended",
  SUSPENDED: "Suspended",
  APPROVED: "Approved",
  DISAPPROVED: "Disapproved",
  UNDER_REVIEW: "Under review",
  UNKNOWN: "Unknown",
  UNSPECIFIED: "Unspecified",
  PUBLISHED: "Published",
  SCHEDULED: "Scheduled",
  FAILED: "Failed",
};

/** Convert API value (e.g. "NOT_ELIGIBLE", "pending") to readable label ("Not eligible") */
const formatStatusLabel = (value) => {
  if (value == null || value === "") return "—";
  const v = String(value).toUpperCase().replace(/-/g, "_");
  return STATUS_LABELS[v] ?? v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Convert reason code (e.g. "MOST_ADS_UNDER_REVIEW") to readable label ("Most ads under review") */
const formatReasonLabel = (reason) => {
  if (reason == null || reason === "") return "";
  const s = String(reason).replace(/-/g, " ").replace(/_/g, " ");
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

// Semantic badge variant from API value (e.g. ENABLED, ELIGIBLE, SERVING → success; PENDING, UNDER_REVIEW → warning; NOT_ELIGIBLE, DISAPPROVED → danger)
const badgeVariant = (value) => {
  if (value == null || value === "") return "neutral";
  const v = String(value).toUpperCase();
  if (["ENABLED", "ACTIVE", "ELIGIBLE", "SERVING", "APPROVED", "PUBLISHED"].includes(v)) return "success";
  if (["PAUSED", "REMOVED", "NONE", "ENDED", "SUSPENDED"].includes(v)) return "neutral";
  if (["PENDING", "UNDER_REVIEW", "UNKNOWN", "UNSPECIFIED"].includes(v)) return "warning";
  if (["NOT_ELIGIBLE", "DISAPPROVED", "DRAFT", "FAILED"].includes(v)) return "danger";
  return "neutral";
};

const StatusBadge = ({ value }) => {
  const variant = badgeVariant(value);
  const displayText = formatStatusLabel(value);
  const rawText = displayValue(value);
  return (
    <span className={`ga-status-block__badge ga-status-block__badge--${variant}`} title={rawText}>
      {displayText}
    </span>
  );
};

/**
 * Reusable Google Ads status block: card with label/value rows (values as badges), optional reasons as tags, Sync button.
 * @param {string} title - Section title (e.g. "Google status")
 * @param {Array<{ label: string, value: any }>} rows - Pairs to show (e.g. [{ label: "Status", value: "ENABLED" }])
 * @param {string[]} reasons - primary_status_reasons (optional)
 * @param {() => void} onSync - Called when Sync status is clicked
 * @param {boolean} syncing - Disable button and show "Syncing…"
 * @param {boolean} showSyncButton - Default true
 */
const GoogleStatusBlock = ({
  title = "Google status",
  rows = [],
  reasons = [],
  onSync,
  syncing = false,
  showSyncButton = true,
}) => {
  return (
    <div className="ga-status-block">
      <div className="ga-status-block__header">
        <h3 className="ga-status-block__title">{title}</h3>
        {showSyncButton && onSync && (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm ga-status-block__sync"
            onClick={onSync}
            disabled={syncing}
          >
            {syncing ? "Syncing…" : "Sync status"}
          </button>
        )}
      </div>
      <div className="ga-status-block__grid">
        {rows.map(({ label, value }) => (
          <div key={label} className="ga-status-block__row">
            <span className="ga-status-block__label">{label}</span>
            <StatusBadge value={value} />
          </div>
        ))}
      </div>
      {Array.isArray(reasons) && reasons.length > 0 && (
        <div className="ga-status-block__reasons">
          <span className="ga-status-block__reasons-label">Reasons</span>
          <div className="ga-status-block__reasons-list">
            {reasons.map((r, i) => (
              <span key={i} className="ga-status-block__reason-tag">
                {formatReasonLabel(r) || r}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleStatusBlock;
export { displayValue, StatusBadge, badgeVariant, formatStatusLabel, formatReasonLabel };
